import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import api from "@/lib/api";
import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { DashboardLayout } from "@/components/DashboardLayout";

import Website from "@/pages/Website";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Quotes from "@/pages/Quotes";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Logistics from "@/pages/Logistics";
import LiveTrack from "@/pages/LiveTrack";
import Financial from "@/pages/Financial";
import Referrals from "@/pages/Referrals";
import System from "@/pages/System";
import Marketplace from "@/pages/Marketplace";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Clients from "@/pages/Clients";
import Partners from "@/pages/Partners";
import PartnersMap from "@/pages/PartnersMap";
import PartnerDetail from "@/pages/PartnerDetail";
import CompanyProfile from "@/pages/CompanyProfile";
import Drivers from "@/pages/Drivers";
import Vehicles from "@/pages/Vehicles";
import Settings from "@/pages/Settings";
import Audit from "@/pages/Audit";
import PaymentReturn from "@/pages/PaymentReturn";
import AccountSetup from "@/pages/AccountSetup";
import Account from "@/pages/Account";
import Calculator from "@/pages/Calculator";
import SEOPages from "@/pages/SEOPages";
import SeoLanding from "@/pages/SeoLanding";
import AuthCallback from "@/pages/AuthCallback";
import ManualQuote from "@/pages/ManualQuote";

const dash = (el, roles) => (
  <ProtectedRoute roles={roles}>
    <DashboardLayout>{el}</DashboardLayout>
  </ProtectedRoute>
);

function App() {
  useEffect(() => {
    document.title = "Rimpatrio24";
    api.get("/public/settings").then(({ data }) => {
      const fav = data?.branding?.favicon || data?.branding?.compact_logo;
      if (fav) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
        link.href = `${process.env.REACT_APP_BACKEND_URL}${fav}`;
      }
    }).catch(() => {});
  }, []);
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Returning from Google OAuth: process the one-time session_id BEFORE any route/guard runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
            <Route path="/" element={<Website />} />
            <Route path="/calculator" element={<Calculator />} />
            <Route path="/preventivo" element={<Calculator />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/payment-return" element={<PaymentReturn />} />
            <Route path="/set-password" element={<AccountSetup mode="password" />} />
            <Route path="/magic-login" element={<AccountSetup mode="magic" />} />
            <Route path="/track-live/:token" element={<LiveTrack />} />

            <Route path="/dashboard" element={dash(<Dashboard />)} />
            <Route path="/quotes" element={dash(<Quotes />, ["admin", "support"])} />
            <Route path="/manual-quote" element={dash(<ManualQuote />, ["admin", "support"])} />
            <Route path="/orders" element={dash(<Orders />)} />
            <Route path="/orders/:id" element={dash(<OrderDetail />)} />
            <Route path="/logistics" element={dash(<Logistics />, ["admin", "support"])} />
            <Route path="/financial" element={dash(<Financial />, ["admin", "support"])} />
            <Route path="/referrals" element={dash(<Referrals />, ["admin", "support", "partner"])} />
            <Route path="/marketplace" element={dash(<Marketplace />, ["admin", "support", "partner"])} />
            <Route path="/system" element={dash(<System />, ["admin"])} />
            <Route path="/invoices" element={dash(<Invoices />, ["admin", "support", "partner", "client"])} />
            <Route path="/invoices/:id" element={dash(<InvoiceDetail />, ["admin", "support", "partner", "client"])} />
            <Route path="/clients" element={dash(<Clients />, ["admin", "support"])} />
            <Route path="/partners" element={dash(<Partners />, ["admin", "support"])} />
            <Route path="/partners-map" element={dash(<PartnersMap />, ["admin", "support"])} />
            <Route path="/partners/:id" element={dash(<PartnerDetail />, ["admin", "support"])} />
            <Route path="/company" element={dash(<CompanyProfile />, ["partner"])} />
            <Route path="/drivers" element={dash(<Drivers />, ["admin", "support", "partner"])} />
            <Route path="/vehicles" element={dash(<Vehicles />, ["admin", "support", "partner"])} />
            <Route path="/settings" element={dash(<Settings />, ["admin"])} />
            <Route path="/seo" element={dash(<SEOPages />, ["admin", "support"])} />
            <Route path="/audit" element={dash(<Audit />, ["admin", "support"])} />
            <Route path="/account" element={dash(<Account />)} />
            <Route path="/:slug" element={<SeoLanding />} />
          </Routes>
  );
}

export default App;
